import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { X, RotateCcw, Save, Loader2, Camera } from 'lucide-react';
import { toast } from 'sonner';
import api from '../lib/api';
import { RARITY_UI } from '../lib/rarity';

/**
 * <ArenaFrameTuner>
 *
 * A live vignette-preview tuner for a battle card's real-life photo.
 * Explorers drag three sliders (X / Y / Scale) and see the exact
 * treatment `<LiveArena>` will apply during a tap-battle in real time.
 *
 * `PATCH /cards/:id/frame` persists `{x, y, scale}` on the card doc;
 * the battle engine reads the same field into `challenger_snapshot`
 * so the effect follows the card everywhere — vault, PvP, guild raids.
 */
const DEFAULT_FRAME = { x: 50, y: 55, scale: 100 };

export default function ArenaFrameTuner({ card, onClose, onSaved }) {
  const [frame, setFrame] = useState(() => ({ ...DEFAULT_FRAME, ...(card?.frame || {}) }));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    // Re-sync when the caller opens the tuner for a different card.
    setFrame({ ...DEFAULT_FRAME, ...(card?.frame || {}) });
  }, [card?.card_id]);

  if (!card) return null;
  const ui = RARITY_UI[card.rarity] || RARITY_UI.common;
  const color = ui.color || '#E9C46A';
  // Same resolution as Vault#cardImageUrl — prefer the explorer's real
  // photo (image_url / image_path), fall back to Nano-Banana portrait.
  const raw = card.image_url || card.image_path;
  const picture = raw
    ? (raw.startsWith('http://') || raw.startsWith('https://') || raw.startsWith('/api/files/')
        ? raw : `/api/files/${raw}`)
    : (card.art_url || null);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/cards/${card.card_id}/frame`, frame);
      toast.success('Arena framing saved');
      onSaved?.(data.frame);
      onClose?.();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    setSaving(true);
    try {
      await api.post(`/cards/${card.card_id}/frame`, {});
      toast.message('Framing reset to defaults');
      onSaved?.(null);
      setFrame(DEFAULT_FRAME);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Reset failed');
    } finally {
      setSaving(false);
    }
  };

  // The exact mask formula shipped by <LiveArena>. Keeping this in
  // sync so what you see IS what fires in battle.
  const maskCss = `radial-gradient(ellipse 78% 88% at ${frame.x}% ${frame.y}%, #000 55%, rgba(0,0,0,0.85) 72%, rgba(0,0,0,0) 100%)`;

  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      className="fixed inset-0 z-[110] bg-black/85 backdrop-blur-md p-5 overflow-y-auto"
      onClick={onClose}
      data-testid="arena-frame-tuner"
    >
      <motion.div
        initial={{ y: 20, opacity: 0, scale: 0.95 }} animate={{ y: 0, opacity: 1, scale: 1 }} exit={{ y: 20, opacity: 0 }}
        transition={{ type: 'spring', stiffness: 300, damping: 24 }}
        onClick={e => e.stopPropagation()}
        className="max-w-md mx-auto"
      >
        <div className="rounded-3xl bg-card border border-white/10 overflow-hidden">
          <div className="flex items-center justify-between px-5 py-3 border-b border-white/5">
            <div className="flex items-center gap-2">
              <Camera className="w-4 h-4 text-accent" />
              <div>
                <p className="text-[10px] uppercase tracking-widest text-accent">Arena Framing</p>
                <p className="text-sm font-serif">{card.species_common_name}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              data-testid="frame-tuner-close"
              className="p-1.5 rounded-full bg-white/10 hover:bg-white/20"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Live preview stage — mimics the LiveArena Creature slab. */}
          <div className="relative mx-auto"
               style={{ width: '82%', aspectRatio: '3 / 4', marginTop: 20, marginBottom: 20 }}
               data-testid="frame-tuner-preview">
            <div
              className="absolute inset-0 rounded-3xl"
              style={{
                background:
                  'radial-gradient(ellipse at 50% 100%, rgba(255,255,255,0.06) 0%, transparent 70%),' +
                  'linear-gradient(180deg, #04120E 0%, #0A2A22 100%)',
                boxShadow: `inset 0 0 40px ${color}22, inset 0 -30px 30px rgba(0,0,0,0.6)`,
              }}
            />
            {/* Ground disc — matches the arena floor. */}
            <div className="absolute inset-x-6 bottom-4 h-4 rounded-full"
                 style={{ background: `radial-gradient(ellipse at center, ${color}55 0%, transparent 70%)`, filter: 'blur(3px)' }}
            />
            {picture ? (
              <>
                <img
                  src={picture}
                  alt=""
                  className="absolute inset-0 w-full h-full object-contain object-bottom pointer-events-none"
                  style={{
                    transform: `scale(${frame.scale / 100})`,
                    transformOrigin: '50% 100%',
                    filter: `drop-shadow(0 12px 10px rgba(0,0,0,0.7)) drop-shadow(0 0 22px ${color}77) contrast(1.06) saturate(1.1)`,
                    WebkitMaskImage: maskCss,
                    maskImage: maskCss,
                  }}
                />
                <div className="absolute inset-0 pointer-events-none"
                  style={{
                    background: `radial-gradient(ellipse at ${frame.x}% ${frame.y - 10}%, ${color}22 0%, transparent 60%)`,
                    mixBlendMode: 'screen',
                    opacity: 0.55,
                  }}
                />
                <div className="absolute inset-x-0 bottom-0 h-1/3 pointer-events-none rounded-b-3xl"
                  style={{ background: 'linear-gradient(180deg, rgba(4,10,8,0) 0%, rgba(4,10,8,0.55) 70%, rgba(4,10,8,0.85) 100%)' }}
                />
                {/* Crosshair marker showing the vignette centre. */}
                <div className="absolute pointer-events-none"
                  style={{
                    left: `${frame.x}%`, top: `${frame.y}%`,
                    transform: 'translate(-50%, -50%)',
                  }}
                  data-testid="frame-crosshair"
                >
                  <div className="w-6 h-6 rounded-full border border-accent/70"
                       style={{ boxShadow: '0 0 8px rgba(233,196,106,0.55)' }} />
                </div>
              </>
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-xs text-muted-foreground">
                No photo on this card
              </div>
            )}
          </div>

          {/* Sliders */}
          <div className="px-5 pb-4 space-y-3">
            <SliderRow
              label="Horizontal (X)"
              value={frame.x}
              min={0} max={100} step={1}
              onChange={v => setFrame(f => ({ ...f, x: v }))}
              testid="frame-slider-x"
              suffix="%"
            />
            <SliderRow
              label="Vertical (Y)"
              value={frame.y}
              min={0} max={100} step={1}
              onChange={v => setFrame(f => ({ ...f, y: v }))}
              testid="frame-slider-y"
              suffix="%"
            />
            <SliderRow
              label="Scale"
              value={frame.scale}
              min={80} max={140} step={1}
              onChange={v => setFrame(f => ({ ...f, scale: v }))}
              testid="frame-slider-scale"
              suffix="%"
            />
          </div>

          <div className="px-5 pb-5 flex items-center gap-2">
            <button
              onClick={reset}
              disabled={saving}
              data-testid="frame-tuner-reset"
              className="flex-1 py-2.5 rounded-xl bg-white/5 hover:bg-white/10 text-xs text-white/70 flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              <RotateCcw className="w-3.5 h-3.5" /> Reset
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="frame-tuner-save"
              className="flex-[2] py-2.5 rounded-xl bg-accent text-black text-xs font-semibold hover:bg-accent/90 disabled:opacity-60 flex items-center justify-center gap-1.5"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              Save framing
            </button>
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function SliderRow({ label, value, onChange, min, max, step, testid, suffix = '' }) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
        <span className="text-xs font-mono text-white/80">{value}{suffix}</span>
      </div>
      <input
        type="range"
        min={min} max={max} step={step}
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        data-testid={testid}
        className="w-full h-1.5 rounded-full bg-white/10 accent-accent"
      />
    </div>
  );
}
